import { Box, Button, Chip, CircularProgress, Divider, FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import BetlistShortDto from "../models/dtos/BetlistShortDto";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { getBalancePaginated, getBetlistsPaginated, getChatMessagesById, getMessageTemplates, getSupportChatsForUser, getSupportChatById, getTemplates, sendMessageToUser, closeSupportChat, reopenSupportChat, createSupportChatForUser, toggleSupportChatPriority, translateMessage, getChatAISummary, generateChatAISummary, uploadSupportImage } from "../Api";
import { composeSupportMessage, MAX_SUPPORT_PHOTOS, prepareSupportPhoto } from "../utils/supportPhotos";
import { SUPPORTED_LANGUAGES } from "../utils/languages";
import BalanceHistoryRecordDto from "../models/dtos/BalanceHistoryRecordDto";
import SupportMessageWithCommentCountDto from "../models/dtos/SupportMessageWithCommentCountDto";
import MessageCard from "./MessageCard";
import MessageTemplateDto from "../models/dtos/MessageTemplateDto";
import SupportChatDto from "../models/dtos/SupportChatDto";
import CreateTicketModal from "./CreateTicketModal";
import AIChatSummaryDto from "../models/dtos/AIChatSummaryDto";

interface SupportChatTableProps {
    userId: number
    chatId?: number | null
}

/** A photo picked or pasted for the next reply: the JPEG to upload and a local preview url. */
interface PendingPhoto {
    blob: Blob
    preview: string
}

export default function SupportChatTable(props: SupportChatTableProps) {

    // Outside `state`: the table unmounts while it reloads, the photos must survive a failed send.
    const [pendingPhotos, setPendingPhotos] = React.useState<PendingPhoto[]>([]);
    const [preparingPhotos, setPreparingPhotos] = React.useState(false);
    React.useEffect(() => () => pendingPhotos.forEach(p => URL.revokeObjectURL(p.preview)), []); // eslint-disable-line react-hooks/exhaustive-deps

    const addPhotos = async (files: File[]) => {
        const images = files.filter(f => f.type.startsWith('image/'));
        const room = MAX_SUPPORT_PHOTOS - pendingPhotos.length;
        if (images.length === 0) return;
        if (room <= 0) {
            alert(`Up to ${MAX_SUPPORT_PHOTOS} photos per message.`);
            return;
        }
        if (images.length > room) alert(`Up to ${MAX_SUPPORT_PHOTOS} photos per message: only the first ${room} were added.`);
        setPreparingPhotos(true);
        const prepared: PendingPhoto[] = [];
        for (const file of images.slice(0, room)) {
            const blob = await prepareSupportPhoto(file);
            if (blob == null) {
                alert(`"${file.name}" can't be read as an image.`);
                continue;
            }
            prepared.push({ blob, preview: URL.createObjectURL(blob) });
        }
        setPreparingPhotos(false);
        setPendingPhotos(current => [...current, ...prepared]);
    }

    const removePhoto = (index: number) => {
        setPendingPhotos(current => {
            URL.revokeObjectURL(current[index].preview);
            return current.filter((_, i) => i !== index);
        });
    }

    const [state, setState] = React.useState({
        chats: [] as SupportChatDto[],
        selectedChatId: null as number | null,
        currentChatInfo: null as SupportChatDto | null,
        messages: [] as SupportMessageWithCommentCountDto[],
        loaded: false,
        selectedTemplateLanguage: 'ru',
        selectedTemplate: 'Select template',
        templateLanguages: [] as string[],
        templates: [] as string[],
        templateCache: [] as MessageTemplateDto[],
        sendText: '',
        createTicketModalOpen: false,
        selectedTranslateLanguage: '',
        isTranslating: false,
        chatSummary: null as AIChatSummaryDto | null,
        isGeneratingSummary: false
    });


    const fetchData = async () => {
        let [chatsResult, templates] = await Promise.all([getSupportChatsForUser(props.userId, 0, 100), getMessageTemplates()]);
        let chats = chatsResult?.content ?? [];
        
        // Find the chat to select based on props.chatId, active chat, or default to first
        let selectedChatId: number | null = null;
        if (props.chatId && chats.find(c => c.chatId === props.chatId)) {
            // If a specific chatId was provided in props and exists, use it
            selectedChatId = props.chatId;
        } else {
            // Otherwise, find the active chat or default to the first one
            let activeChat = chats.find(c => c.active);
            selectedChatId = activeChat?.chatId ?? (chats.length > 0 ? chats[0].chatId : null);
        }
        
        let langs = templates?.map(i => i.language) ?? [];
        let templateLanguage = langs[0];
        let templateValues = templates?.find(t => t.language == templateLanguage)?.values ?? [];
        
        setState(prevState => ({ 
            ...prevState, 
            chats: chats,
            selectedChatId: selectedChatId,
            selectedTemplateLanguage: templateLanguage, 
            templates: templateValues, 
            templateLanguages: langs, 
            templateCache: templates ?? [],
            loaded: false
        }));
        
        // Load messages for the selected chat
        if (selectedChatId !== null) {
            await loadMessagesForChat(selectedChatId);
        } else {
            setState(prevState => ({ ...prevState, loaded: true, messages: [] }));
        }
    }

    const loadMessagesForChat = async (chatId: number) => {
        setState(prevState => ({ ...prevState, loaded: false }));
        let [records, chatInfo, summary] = await Promise.all([
            getChatMessagesById(props.userId, chatId, 0, 100),
            getSupportChatById(props.userId, chatId),
            getChatAISummary(props.userId, chatId)
        ]);
        setState(prevState => ({ 
            ...prevState, 
            messages: records?.content ?? [],
            currentChatInfo: chatInfo,
            selectedTranslateLanguage: chatInfo?.language || '',
            chatSummary: summary,
            loaded: true
        }));
    }

    const onChatSelected = async (chatId: number) => {
        setState(prevState => ({ ...prevState, selectedChatId: chatId }));
        await loadMessagesForChat(chatId);
    }

    const navigate = useNavigate();

    const getBalanceBackground = (oldBalance: number, newBalance: number) => {
        if (oldBalance > newBalance)
            return '#FFCCCB';
        else
            return 'lightGreen';
    }

    const sendMessageClicked = async () => {
        const words = state.sendText ?? '';
        // Photos speak for themselves; words alone need a few letters.
        if (pendingPhotos.length === 0 && words.length < 3) {
            alert("Text is too short!");
            return;
        }
        if (state.selectedChatId === null) {
            alert("No chat selected!");
            return;
        }
        if (preparingPhotos) return;
        const chatId = state.selectedChatId;
        setState(prevState => ({...prevState, loaded: false}));
        const urls: string[] = [];
        for (const photo of pendingPhotos) {
            const uploaded = await uploadSupportImage(photo.blob);
            if ('error' in uploaded) {
                // Words and photos stay for another try.
                setState(prevState => ({...prevState, loaded: true}));
                alert("Photo upload failed: " + uploaded.error);
                return;
            }
            urls.push(uploaded.url);
        }
        let result = await sendMessageToUser(props.userId, chatId, composeSupportMessage(words, urls));
        if (result) {
            pendingPhotos.forEach(p => URL.revokeObjectURL(p.preview));
            setPendingPhotos([]);
            setState(prevState => ({...prevState, sendText: ''}));
            await loadMessagesForChat(chatId);
        } else {
            setState(prevState => ({...prevState, loaded: true}));
            alert("Somthing went wrong!");
        }
    }

    const handleTranslateAndEnhance = async () => {
        if (state.sendText == null || state.sendText.length < 3) {
            alert("Text is too short to translate!");
            return;
        }
        if (!state.selectedTranslateLanguage) {
            alert("Please select a language!");
            return;
        }
        setState(prevState => ({...prevState, isTranslating: true}));
        const result = await translateMessage(state.sendText, state.selectedTranslateLanguage);
        if (result && result.translatedMessage) {
            setState(prevState => ({
                ...prevState, 
                sendText: result.translatedMessage,
                isTranslating: false
            }));
        } else {
            setState(prevState => ({...prevState, isTranslating: false}));
            alert("Translation failed!");
        }
    }

    const closeChatClicked = async () => {
        if (state.selectedChatId === null) {
            alert("No chat selected!");
            return;
        }
        if (!window.confirm("Are you sure you want to close this chat?")) {
            return;
        }
        setState(prevState => ({...prevState, loaded: false}));
        let result = await closeSupportChat(props.userId, state.selectedChatId);
        if (result) {
            await fetchData();
        } else {
            setState(prevState => ({...prevState, loaded: true}));
            alert("Something went wrong!");
        }
    }

    const reopenChatClicked = async () => {
        if (state.selectedChatId === null) {
            alert("No chat selected!");
            return;
        }
        if (!window.confirm("Are you sure you want to reopen this chat?")) {
            return;
        }
        setState(prevState => ({...prevState, loaded: false}));
        let result = await reopenSupportChat(props.userId, state.selectedChatId);
        if (result) {
            await fetchData();
        } else {
            setState(prevState => ({...prevState, loaded: true}));
            alert("Something went wrong!");
        }
    }

    const togglePriorityClicked = async () => {
        if (state.selectedChatId === null) {
            alert("No chat selected!");
            return;
        }
        setState(prevState => ({...prevState, loaded: false}));
        let result = await toggleSupportChatPriority(props.userId, state.selectedChatId);
        if (result) {
            await fetchData();
        } else {
            setState(prevState => ({...prevState, loaded: true}));
            alert("Something went wrong!");
        }
    }

    const onTemplateLanguageSelected = (langTemplate: string) => {
        let templateValues = state.templateCache?.find(t => t.language == langTemplate)?.values ?? [];
        setState(prevState => ({ ...prevState, templates: templateValues, selectedTemplateLanguage: langTemplate }));
    }

    const onTemplateSelected = (template: string) => {
        setState(prevState => ({ ...prevState, selectedTemplate: template, sendText: template }));
    }

    const handleCreateTicket = async (chatName: string, message: string, categoryId: string) => {
        setState(prevState => ({...prevState, loaded: false}));
        let result = await createSupportChatForUser(props.userId, chatName, message, categoryId);
        if (result) {
            await fetchData();
        } else {
            setState(prevState => ({...prevState, loaded: true}));
            alert("Something went wrong while creating the ticket!");
        }
    }

    const openCreateTicketModal = () => {
        setState(prevState => ({...prevState, createTicketModalOpen: true}));
    }

    const closeCreateTicketModal = () => {
        setState(prevState => ({...prevState, createTicketModalOpen: false}));
    }

    const handleGenerateSummary = async () => {
        if (state.selectedChatId === null) {
            alert("No chat selected!");
            return;
        }
        setState(prevState => ({...prevState, isGeneratingSummary: true}));
        const result = await generateChatAISummary(props.userId, state.selectedChatId);
        if (result) {
            setState(prevState => ({
                ...prevState, 
                chatSummary: result,
                isGeneratingSummary: false
            }));
        } else {
            setState(prevState => ({...prevState, isGeneratingSummary: false}));
            alert("Failed to generate summary!");
        }
    }

    React.useEffect(() => {
        fetchData();
    }, []);

    const getSelectedChat = () => {
        return state.currentChatInfo;
    }

    let text = "fdsfdsfsdf";

    return (
        <Stack>
            {!state.loaded ? <CircularProgress size='100px' style={{ marginBottom: '32px' }} /> :
                <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 6 }} alignItems='center'>
                    {state.chats.length > 0 && (
                        <>
                            <Grid item xs={1} sm={2} md={3} lg={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="chat-select-label">Select Chat</InputLabel>
                                    <Select
                                        fullWidth={true}
                                        labelId="chat-select-label"
                                        id="chat-select"
                                        value={state.selectedChatId ?? ''}
                                        onChange={e => onChatSelected(e.target.value as number)}
                                        label="Select Chat">
                                        {
                                            state.chats.map(chat => (
                                                <MenuItem key={chat.chatId} value={chat.chatId}>
                                                    {chat.priority && '❗ '}
                                                    {chat.category && `[${chat.category.name}] `}
                                                    {chat.chatName} - {new Date(chat.lastMessageDate).toLocaleDateString()} - {chat.active ? '🟢 Active' : '⚫ Closed'}
                                                </MenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={1} sm={1} md={1} lg={1}>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    fullWidth={true} 
                                    style={{ height: '54px' }} 
                                    onClick={openCreateTicketModal}>
                                    Add New Ticket
                                </Button>
                            </Grid>
                            <Grid item xs={1} sm={1} md={1} lg={1}>
                                <Button
                                    component={Link}
                                    to={'/ai/conversations/support-chat/' + state.selectedChatId}
                                    variant="outlined"
                                    color="primary"
                                    fullWidth={true}
                                    style={{ height: '54px' }}
                                    disabled={!state.selectedChatId}
                                    title="Betty's side of this chat in the AI hub: her tool calls, results, replies and summary">
                                    AI conversation
                                </Button>
                            </Grid>
                            <Box width="100%" />
                        </>
                    )}
                    {state.chats.length === 0 && (
                        <>
                            <Grid item xs={1} sm={2} md={4} lg={6}>
                                <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                                    <Box>No chats available.</Box>
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        onClick={openCreateTicketModal}>
                                        Create Ticket
                                    </Button>
                                </Box>
                            </Grid>
                            <Box width="100%" />
                        </>
                    )}
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <FormControl fullWidth>
                            <InputLabel id="translate-language-label">Language</InputLabel>
                            <Select
                                labelId="translate-language-label"
                                id="translate-language"
                                value={state.selectedTranslateLanguage}
                                onChange={e => setState(prevState => ({ ...prevState, selectedTranslateLanguage: e.target.value }))}
                                label="Language"
                                style={{ height: '54px' }}>
                                {
                                    SUPPORTED_LANGUAGES.map(lang => (
                                        <MenuItem key={lang.code} value={lang.code}>
                                            {lang.code} ({lang.name})
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Button 
                            variant="outlined" 
                            color="primary" 
                            fullWidth={true} 
                            style={{ height: '54px' }} 
                            onClick={handleTranslateAndEnhance}
                            disabled={state.isTranslating || !state.sendText || state.sendText.length < 3}>
                            {state.isTranslating ? 'Translating...' : 'Translate & Enhance'}
                        </Button>
                    </Grid>
                    <Box width="100%" />
                    <Grid item xs={1} sm={2} md={4} lg={6}>
                        <TextField fullWidth={true} maxRows={5} rows={5} multiline value={state.sendText}
                            placeholder="Reply… (paste a screenshot to attach it)"
                            onChange={e => setState(prevState => ({ ...prevState, sendText: e.target.value }))}
                            onPaste={e => {
                                const files = Array.from(e.clipboardData.files);
                                if (files.some(f => f.type.startsWith('image/'))) {
                                    e.preventDefault();
                                    addPhotos(files);
                                }
                            }} />
                    </Grid>
                    <Grid item xs={1} sm={2} md={4} lg={6}>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={preparingPhotos ? <CircularProgress size={18} /> : <AddPhotoAlternateIcon />}
                                disabled={preparingPhotos || pendingPhotos.length >= MAX_SUPPORT_PHOTOS}>
                                Attach photos ({pendingPhotos.length}/{MAX_SUPPORT_PHOTOS})
                                <input hidden type="file" accept="image/*" multiple
                                    onChange={e => {
                                        const files = Array.from(e.target.files ?? []);
                                        e.target.value = '';
                                        addPhotos(files);
                                    }} />
                            </Button>
                            {pendingPhotos.map((photo, i) => (
                                <Box key={photo.preview} sx={{ position: 'relative' }}>
                                    <Box component="img" src={photo.preview} alt={`Attachment ${i + 1}`}
                                        sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid rgba(0,0,0,0.2)' }} />
                                    <IconButton size="small" onClick={() => removePhoto(i)} title="Remove"
                                        sx={{ position: 'absolute', top: -10, right: -10, backgroundColor: 'white', boxShadow: 1, '&:hover': { backgroundColor: '#eee' } }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Button variant="contained" fullWidth={true} style={{ height: '54px' }} disabled={preparingPhotos} onClick={() => sendMessageClicked()}>Send</Button>
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        {getSelectedChat()?.active ? (
                            <Button variant="contained" color="success" fullWidth={true} style={{ height: '54px' }} onClick={() => closeChatClicked()}>Close Ticket</Button>
                        ) : (
                            <Button variant="contained" color="inherit" fullWidth={true} style={{ height: '54px' }} onClick={() => reopenChatClicked()}>Re-open Ticket</Button>
                        )}
                    </Grid>
                    {getSelectedChat()?.active && (
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <Button 
                                variant="contained" 
                                color={getSelectedChat()?.priority ? "warning" : "info"} 
                                fullWidth={true} 
                                style={{ height: '54px' }} 
                                onClick={() => togglePriorityClicked()}>
                                {getSelectedChat()?.priority ? 'Remove Priority' : 'Add Priority'}
                            </Button>
                        </Grid>
                    )}
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <FormControl fullWidth>
                            <InputLabel id="template-language">{"Select template language"}</InputLabel>
                            <Select
                                fullWidth={true}
                                labelId="tempalte-select-lang"
                                id="t1"
                                value={state.selectedTemplateLanguage}
                                onChange={e => onTemplateLanguageSelected(e.target.value)}
                                label="Language">
                                {
                                    state.templateLanguages.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <FormControl fullWidth>
                            <InputLabel id="template-value">{"Select template text"}</InputLabel>
                            <Select
                                fullWidth={true}
                                labelId="tempate-select-value"
                                id="t2"
                                value={state.selectedTemplate}
                                onChange={e => onTemplateSelected(e.target.value)}
                                label="Template">
                                {
                                    state.templates.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Box width="100%" />
                    <Grid item xs={1} sm={2} md={4} lg={6}>
                        <Divider />
                    </Grid>
                    <Box width="100%" />
                    {state.chatSummary && (
                        <>
                            <Grid item xs={1} sm={2} md={4} lg={6}>
                                <Paper 
                                    elevation={2} 
                                    sx={{ 
                                        padding: 2, 
                                        backgroundColor: '#FFF9C4',
                                        border: '1px solid #FDD835'
                                    }}>
                                    <Box sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                                        💡 AI Summary
                                    </Box>
                                    <Box sx={{ whiteSpace: 'pre-wrap' }}>
                                        {state.chatSummary.summary}
                                    </Box>
                                </Paper>
                            </Grid>
                            <Box width="100%" />
                        </>
                    )}
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Button 
                            variant="contained" 
                            color="secondary" 
                            fullWidth={true} 
                            style={{ height: '54px' }} 
                            onClick={handleGenerateSummary}
                            disabled={state.isGeneratingSummary || !state.selectedChatId}>
                            {state.isGeneratingSummary ? <CircularProgress size={24} /> : 'Generate Summary'}
                        </Button>
                    </Grid>

                    <Box width="100%" />
                    <Grid item xs={1} sm={2} md={4} lg={6}>
                        <Table>
                            <TableBody>
                                {state.messages.map(item => (
                                    <React.Fragment key={item.message.id}>
                                        <TableRow>
                                            <TableCell sx={{ borderBottom: "none", width: '45%' }}>
                                                {item.message.user.id != props.userId ?
                                                    <MessageCard message={item.message} align='left' /> : <div />}
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none", width:'10%' }}>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none",  width: '45%' }}>
                                                {item.message.user.id == props.userId ?
                                                    <MessageCard message={item.message} align="right" /> : <div />}
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </Grid>
                </Grid>}
            <CreateTicketModal 
                open={state.createTicketModalOpen} 
                onClose={closeCreateTicketModal} 
                onSubmit={handleCreateTicket} 
            />
        </Stack>);
}